import { z } from 'zod';
import type { Role } from '../permissions/types.js';

// Mirrors APP-106's shape: { everyone, roles, personIds }. Domain doesn't depend on contracts, so
// the role list is the domain's own; the Record forces a new Role to be listed here.
const ROLE_FLAGS: Record<Role, true> = {
  employee: true,
  manager: true,
  sector_lead: true,
  delegate: true,
  executive_delegate: true,
  requester: true,
  executive: true,
  stock_admin: true,
  payroll: true,
  finance: true,
  system_admin: true,
  paa_taa_approver: true,
  spot_reviewer: true,
  support: true,
};
export const AUDIENCE_ROLES = Object.keys(ROLE_FLAGS) as [Role, ...Role[]];
const AudienceRole = z.enum(AUDIENCE_ROLES);

const noDuplicates = (v: { roles: readonly string[]; personIds: readonly string[] }): boolean =>
  new Set(v.roles).size === v.roles.length && new Set(v.personIds).size === v.personIds.length;

export const AudiencePolicy = z
  .object({
    everyone: z.boolean(),
    roles: z.array(AudienceRole).max(AUDIENCE_ROLES.length),
    personIds: z.array(z.uuid()).max(500),
  })
  .strict()
  .refine(noDuplicates, { message: 'duplicate_audience_entry' });
export type AudiencePolicy = z.infer<typeof AudiencePolicy>;

/**
 * Everyone, or any listed role, or any named person. An all-empty policy admits nobody. APP-274:
 * "requester" is how an audience named these people before the list became delegates, so a saved
 * audience, or the email assistant's default, keeps meaning them.
 */
export function inAudience(
  policy: AudiencePolicy,
  who: { personId: string; roles: readonly Role[] },
): boolean {
  return (
    policy.everyone ||
    policy.roles.some(
      (role) =>
        who.roles.includes(role) ||
        (role === 'requester' && who.roles.includes('executive_delegate')),
    ) ||
    policy.personIds.includes(who.personId)
  );
}
